import { dbToGain } from './plan.js';

/**
 * The mix (design, "The graph"): what the master, the duck and the two
 * switches are, from the teacher's switches, the pause, a voice speaking and
 * the viewer's own mute. Pure, so the rules are tested apart from Web Audio.
 *
 *   world layers ─► ambient bus ─► duck ─► ambient switch ─┐
 *   model sounds ─► models bus ─► models duck ─┘            │
 *   voice files  ─► voice bus ─► voice switch ─────────────┴► master ─► limiter ─► speakers
 *
 * The library's models (ADR-043) have a bus of their own under the same
 * "Achtergrondgeluid" switch, ducked less under a voice: a roar must still read.
 */

/** "Soft": Pieter tunes it on a Quest. About −34 LUFS for a world's ambience. */
export const DEFAULT_LEVEL_DB = -8;
/** Under a voice file (ADR-033), and while the teacher has paused ("eyes on me"). */
export const SPEAKING_DUCK_DB = -10;
export const PAUSED_DUCK_DB = -12;
/** The models' bus under a voice file (not −10: a roar must still read); paused as the rest. */
export const MODELS_SPEAKING_DUCK_DB = -6;

/** A safety net, not a tool: the plans keep things soft. */
export const LIMITER = {
  threshold: -14,
  ratio: 20,
  knee: 0,
  attack: 0.003,
  release: 0.25,
} as const;

export type MixInput = {
  /** The teacher's "Achtergrondgeluid". */
  readonly ambient: boolean;
  /** The teacher's "Voorlezen". */
  readonly voice: boolean;
  readonly paused: boolean;
  /** A voice file is playing (ADR-033; the voice player, voice.ts). */
  readonly speaking: boolean;
  /** The viewer's own mute: the demo's and a played lesson's button, `M`, `?sound=0`. */
  readonly localMute: boolean;
};

export type Mix = {
  master: number;
  duck: number;
  /** The models' own duck (ADR-043). */
  models: number;
  ambient: 0 | 1;
  voice: 0 | 1;
};

export function gainsFor(input: MixInput): Mix {
  const duckDb = (input.speaking ? SPEAKING_DUCK_DB : 0) + (input.paused ? PAUSED_DUCK_DB : 0);
  const modelsDb =
    (input.speaking ? MODELS_SPEAKING_DUCK_DB : 0) + (input.paused ? PAUSED_DUCK_DB : 0);
  return {
    master: input.localMute ? 0 : dbToGain(DEFAULT_LEVEL_DB),
    duck: duckDb === 0 ? 1 : dbToGain(duckDb),
    models: modelsDb === 0 ? 1 : dbToGain(modelsDb),
    ambient: input.ambient ? 1 : 0,
    voice: input.voice ? 1 : 0,
  };
}

/** How long a change of one of the mix's gains takes: never a click. */
export function fadeSeconds(
  what: 'switch' | 'speaking' | 'paused' | 'master',
  from: number,
  to: number,
): number {
  const down = to < from;
  if (what === 'switch' || what === 'master') return down ? 0.3 : 2;
  if (what === 'speaking') return down ? 0.15 : 0.6;
  return 0.5;
}
